/**
 * The message inspector.
 *
 * Overlays the table rather than sitting beside it, exactly as the design
 * specifies: a drawer laid out as a flex sibling steals width from the table,
 * and the first column to collapse is Subject — the one people scan.
 */

import { useEffect } from "react";

import { api } from "../api/client";
import { useAsync } from "../hooks";
import { CopyValue } from "../components/CopyValue";
import { Icon } from "../components/Sprites";
import { JsonBlock } from "../components/JsonBlock";
import { StatusGlyph, statusLabel } from "../components/Status";
import { TemplatePreview } from "../components/TemplatePreview";
import { timeOfDay } from "./MessageLog";
import { useSession } from "../session";
import type { MessageDetail, MessageEvent } from "../api/types";

interface DrawerProps {
  messageId: string;
  onClose: () => void;
}

export function MessageDrawer({ messageId, onClose }: DrawerProps) {
  const { can } = useSession();

  const { data, error, loading } = useAsync<MessageDetail>(
    (signal) => api.get<MessageDetail>(`/v1/messages/${encodeURIComponent(messageId)}`, { signal }),
    [messageId],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} />
      <aside className="oc-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <header className="oc-drawer__header">
          <div style={{ minWidth: 0 }}>
            <div className="oc-label" style={{ marginBottom: 8 }}>
              Message
            </div>
            <h2 className="oc-headline-md" id="drawer-title" style={{ marginBottom: 8 }}>
              {data?.subject ?? (loading ? "Loading…" : "Message")}
            </h2>
            <CopyValue value={messageId} label="Copy message id" />
          </div>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" className="oc-nav__icon" />
          </button>
        </header>

        <div className="oc-drawer__body">
          {error && <p className="field-error">{error.message}</p>}

          {data && (
            <>
              <Recipients message={data} />
              <Timeline events={data.events} lastError={data.last_error} />
              <Preview
                html={data.html}
                text={data.text}
                contentRemovedAt={data.content_removed_at}
                canReadContent={can("messages:read_content")}
              />
              <RawPayload events={data.events} canReadContent={can("messages:read_content")} />
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Recipients({ message }: { message: MessageDetail }) {
  return (
    <section className="drawer-section">
      <span className="oc-label">Envelope</span>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px var(--oc-space-3)", margin: 0 }}>
        <dt className="oc-body-sm oc-text-secondary">To</dt>
        <dd style={{ margin: 0, minWidth: 0 }}>
          <CopyValue value={message.to} className="oc-truncate" />
        </dd>

        <dt className="oc-body-sm oc-text-secondary">From</dt>
        <dd className="oc-mono oc-truncate" style={{ margin: 0 }}>
          {message.from}
        </dd>

        {message.provider_message_id && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Provider id</dt>
            <dd style={{ margin: 0, minWidth: 0 }}>
              {/* The identifier to quote when asking a provider what happened. */}
              <CopyValue value={message.provider_message_id} className="oc-truncate" />
            </dd>
          </>
        )}

        {/* Rendered only when recorded.
            A message that has not been sent, or that predates the column, shows
            nothing here rather than the current default — the default may have
            changed since, and this is the field somebody reads to find out what
            actually happened. */}
        {message.sent_via && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Sent via</dt>
            <dd className="sent-via" style={{ margin: 0, minWidth: 0 }}>
              <span className="oc-truncate">{message.sent_via.name}</span>
              <span className="oc-mono-sm oc-text-muted">{message.sent_via.kind}</span>
              {/* The snapshot outlives the configuration, so say so rather than
                  showing a name that no longer resolves to anything. */}
              {!message.sent_via.id && (
                <span className="oc-mono-sm oc-text-muted">· deleted</span>
              )}
            </dd>
          </>
        )}

        <dt className="oc-body-sm oc-text-secondary">Attempts</dt>
        <dd className="oc-mono-sm" style={{ margin: 0 }}>
          {message.attempt_count ?? 0}
        </dd>
      </dl>
    </section>
  );
}

/** The event types a provider diagnostic can belong to. */
const FAILURES = new Set(["bounced", "complained", "failed"]);

function Timeline({ events, lastError }: { events: MessageEvent[]; lastError?: string | null }) {
  // The diagnostic belongs to the event that failed, not to whichever event is
  // last. A message that bounced and was later retried ends with a send, and
  // printing "550 user unknown" underneath that reads as the send having
  // failed — the opposite of what happened.
  const failureIndex = events.findLastIndex((event) => FAILURES.has(event.type));

  if (events.length === 0) {
    return (
      <section className="drawer-section">
        <span className="oc-label">Delivery timeline</span>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          No events recorded yet. The provider reports delivery asynchronously.
        </p>
      </section>
    );
  }

  return (
    <section className="drawer-section">
      <span className="oc-label">Delivery timeline</span>
      <div className="oc-timeline">
        {events.map((event, index) => {
          const at = new Date(event.occurred_at);
          // Only on the failure it explains, and only when there is one — an
          // error with no failure event is reported below the timeline instead.
          const showsError = index === failureIndex;

          return (
            <div className="oc-timeline__item" key={`${event.type}-${event.occurred_at}-${index}`}>
              <span className="oc-timeline__node">
                <StatusGlyph status={event.type} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="oc-timeline__label">
                  {statusLabel(event.type)}
                  {/* Apple Mail Privacy Protection pre-fetches images, so an
                      open it caused is not evidence a human read anything.
                      Marked rather than hidden — the event did happen. */}
                  {event.is_mpp && (
                    <span className="oc-mono-sm oc-text-muted" style={{ marginLeft: 8 }}>
                      Apple MPP — not a human open
                    </span>
                  )}
                </div>
                <div className="oc-timeline__time" title={at.toISOString()}>
                  {timeOfDay(at)}
                </div>
                {showsError && lastError && (
                  <div className="oc-code" style={{ marginTop: 8, padding: "8px 10px", fontSize: 11, lineHeight: "16px" }}>
                    {lastError}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* A recorded error with nothing in the timeline to hang it on — a send
          that failed before any event was written. Still shown: it is usually
          the only explanation of why nothing happened. */}
      {lastError && failureIndex === -1 && (
        <div>
          <div className="oc-label" style={{ marginBottom: 4 }}>
            Last error
          </div>
          <div className="oc-code" style={{ padding: "8px 10px", fontSize: 11, lineHeight: "16px" }}>
            {lastError}
          </div>
        </div>
      )}
    </section>
  );
}

function Preview({
  html,
  text,
  contentRemovedAt,
  canReadContent,
}: {
  html?: string | null;
  text?: string | null;
  contentRemovedAt?: string | null;
  canReadContent: boolean;
}) {
  // Withheld and absent are different facts, and this section used to render
  // neither — it returned null and the panel simply vanished.
  //
  // `messages:read_content` now gates the body, so for an analyst or a viewer
  // this is the normal case rather than an edge one. A section that disappears
  // reads as a bug in the product; a sentence naming the capability reads as a
  // permission, and tells somebody exactly what to ask an administrator for.
  if (!canReadContent) {
    return (
      <section className="drawer-section">
        <span className="oc-label">Rendered preview</span>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          The message body is not shown. Reading what a message said needs the{" "}
          <code className="oc-mono-sm">messages:read_content</code> capability, which is separate
          from reading the log — a body is somebody's invoice or password reset.
        </p>
      </section>
    );
  }

  // Removed by retention is a third fact, and without saying so this section
  // would vanish exactly as a withheld body used to — reading as a message that
  // never had one.
  if (!html && !text && contentRemovedAt) {
    return (
      <section className="drawer-section">
        <span className="oc-label">Rendered preview</span>
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          The body was removed on{" "}
          {new Date(contentRemovedAt).toLocaleDateString(undefined, {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}{" "}
          by this deployment's retention policy. The delivery record above is kept, and so is the
          unsubscribe link in the mail itself.
        </p>
      </section>
    );
  }

  if (!html && !text) return null;

  return (
    <section className="drawer-section">
      <span className="oc-label">Rendered preview</span>

      {html ? (
        // Rendered on the separate preview origin, not in this document. The
        // body was authored by a tenant, and an opaque-origin srcdoc frame here
        // was only ever the interim answer — this is the one the configuration
        // has reserved a hostname for since Phase 3.
        <TemplatePreview html={html} height={280} title="Message preview" />
      ) : (
        <pre className="oc-code" style={{ whiteSpace: "pre-wrap" }}>
          {text}
        </pre>
      )}

      {html && text && (
        <details>
          <summary>
            <Icon name="chevron" className="caret oc-text-secondary" />
            <span className="oc-label">Plain-text alternative</span>
          </summary>
          <pre className="oc-code" style={{ marginTop: "var(--oc-space-3)", whiteSpace: "pre-wrap" }}>
            {text}
          </pre>
        </details>
      )}
    </section>
  );
}

/**
 * The provider's response, verbatim.
 *
 * Developers trust what they can read unaltered, and a normalisation bug is
 * only recoverable if the original survived it.
 */
function RawPayload({ events, canReadContent }: { events: MessageEvent[]; canReadContent: boolean }) {
  const withPayload = events.filter((event) => event.provider_payload != null);
  // The server omits an open's or a click's payload without the capability,
  // because it describes the reader. Said, rather than left to vanish, for the
  // reason the withheld body is.
  const withheld =
    !canReadContent && events.some((event) => event.type === "opened" || event.type === "clicked");
  if (withPayload.length === 0 && !withheld) return null;

  return (
    <section className="drawer-section">
      <details>
        <summary>
          <Icon name="chevron" className="caret oc-text-secondary" />
          <span className="oc-label">Raw provider payload</span>
        </summary>
        <div className="stack" style={{ marginTop: "var(--oc-space-3)" }}>
          {withheld && (
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              Opens and clicks are listed without the provider&rsquo;s details, which name the
              reader&rsquo;s IP address, location and device and the link they clicked. Seeing them
              needs the <code className="oc-mono-sm">messages:read_content</code> capability.
            </p>
          )}
          {withPayload.map((event, index) => (
            <div key={`${event.type}-${index}`}>
              <div className="oc-mono-sm oc-text-muted" style={{ marginBottom: 4 }}>
                {statusLabel(event.type)} · {new Date(event.occurred_at).toISOString()}
              </div>
              <JsonBlock value={event.provider_payload} />
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}
